/**
 * Central toast notifications.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Saved.');
 *   toast.error('Couldn\'t save', { detail: pbErrorMessage(e) });
 *   toast.info('Uploading…', { sticky: true, id: 'upload' });
 *   toast.dismiss('upload');
 *
 * Toasts auto-dismiss (4s success/info, 7s error) unless `sticky: true`.
 * Pass an `id` to replace an earlier toast (e.g. progress → final result).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Icons } from '../components/Icons.jsx';

const ToastCtx = createContext(null);

let _seq = 1;
function nextId() { return `t${_seq++}`; }

const DEFAULT_TIMEOUTS = { success: 4000, info: 4000, error: 7000 };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    const tm = timers.current.get(id);
    if (tm) { clearTimeout(tm); timers.current.delete(id); }
    setToasts((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((kind, message, opts = {}) => {
    const id = opts.id || nextId();
    const sticky = !!opts.sticky;
    const timeout = opts.timeout != null ? opts.timeout : DEFAULT_TIMEOUTS[kind] || 4000;
    const next = { id, kind, message, detail: opts.detail || null, sticky };
    setToasts((arr) => {
      const filtered = arr.filter((t) => t.id !== id);
      return [...filtered, next];
    });
    // Reset any existing timer for this id
    const prev = timers.current.get(id);
    if (prev) clearTimeout(prev);
    if (!sticky && timeout > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), timeout));
    }
    return id;
  }, [dismiss]);

  useEffect(() => () => {
    timers.current.forEach((tm) => clearTimeout(tm));
    timers.current.clear();
  }, []);

  const api = {
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error',   msg, opts),
    info:    (msg, opts) => push('info',    msg, opts),
    dismiss,
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast() must be inside <ToastProvider>');
  return v;
}

function ToastStack({ toasts, onDismiss }) {
  return (
    <div className="toast-stack" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const Ic = toast.kind === 'success' ? Icons.Check
           : toast.kind === 'error'   ? Icons.X
           :                            Icons.Bell;
  const role = toast.kind === 'error' ? 'alert' : 'status';
  return (
    <div className={`toast toast-${toast.kind}`} role={role}>
      <div className="toast-ic"><Ic size={16} stroke={2.6} /></div>
      <div className="toast-body">
        <div className="toast-msg">{toast.message}</div>
        {toast.detail && <div className="toast-detail">{toast.detail}</div>}
      </div>
      <button type="button" className="toast-close" onClick={onDismiss} aria-label="Dismiss">
        <Icons.X size={14} />
      </button>
    </div>
  );
}

/**
 * Convert a thrown error from a PocketBase call (or any fetch) into a
 * user-readable string. Order of preference:
 *   1. Per-field validation messages from PB (e.data.data) — most specific.
 *   2. Recognised HTTP status codes (413 = file too large, 401, 403, 404,
 *      409, 429, 5xx, network/abort) — translated when `t` is provided.
 *   3. PocketBase's own message if it's actually informative (not the
 *      generic "Something went wrong while processing your request.").
 *   4. The raw Error.message.
 *
 * Pass the `t` from useT() to get localized messages; called without `t`
 * the helper falls back to English defaults.
 */
const PB_GENERIC_MESSAGES = new Set([
  'Something went wrong while processing your request.',
  'Failed to create record.',
  'Failed to update record.',
  'Failed to delete record.',
]);

const STATUS_KEYS = {
  400: 'validation',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'notFound',
  408: 'timeout',
  409: 'conflict',
  413: 'tooLarge',
  422: 'validation',
  429: 'serverError', // rate limit — treat like server issue, retryable
  500: 'serverError',
  502: 'serverError',
  503: 'serverError',
  504: 'timeout',
};

const FALLBACK_EN = {
  tooLarge: 'The file is too large for the server. Try a smaller file (under ~5 MB).',
  unauthorized: 'Your session expired — please sign in again.',
  forbidden: 'You don\'t have permission to do this.',
  notFound: 'Not found — it may have been deleted.',
  conflict: 'Conflict — refresh and try again.',
  validation: 'Some fields are invalid.',
  network: 'Couldn\'t reach the server. Check your connection.',
  timeout: 'The server took too long to respond.',
  serverError: 'The server hit a problem. Please try again.',
  offline: 'You appear to be offline.',
  unknown: 'Unexpected error.',
};

function lookup(t, key) {
  if (t) {
    const out = t('errors.' + key);
    if (out && out !== 'errors.' + key) return out;
  }
  return FALLBACK_EN[key] || FALLBACK_EN.unknown;
}

export function pbErrorMessage(e, t) {
  if (!e) return lookup(t, 'unknown');
  if (typeof e === 'string') return e;

  // 1. Per-field validation — most useful, surface even on 400/422.
  const fields = e?.data?.data && typeof e.data.data === 'object' ? e.data.data : null;
  if (fields && Object.keys(fields).length > 0) {
    const parts = Object.entries(fields).map(([k, v]) => {
      const msg = v?.message || v?.code || 'invalid';
      return `${k}: ${msg}`;
    });
    return parts.join(' · ');
  }

  // 2. Browser offline / network / abort.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return lookup(t, 'offline');
  }
  if (e.name === 'AbortError' || e.isAbort) {
    return lookup(t, 'timeout');
  }
  // PocketBase wraps low-level fetch failures: `e.originalError` is a
  // TypeError("Failed to fetch") when the server is unreachable.
  const orig = e.originalError;
  if (orig && (orig.name === 'TypeError' || /failed to fetch|networkerror/i.test(orig.message || ''))) {
    return lookup(t, 'network');
  }

  // 3. Recognised HTTP status.
  const status = typeof e.status === 'number' ? e.status : (e?.response?.status || 0);
  if (status && STATUS_KEYS[status]) {
    return lookup(t, STATUS_KEYS[status]);
  }
  if (status >= 500 && status < 600) return lookup(t, 'serverError');
  if (status === 0) return lookup(t, 'network');

  // 4. PB top-level message — but only if it actually says something useful.
  const pbMsg = e?.data?.message;
  if (pbMsg && !PB_GENERIC_MESSAGES.has(pbMsg.trim())) {
    return pbMsg;
  }

  // 5. Raw Error.message as last resort.
  if (e.message && !PB_GENERIC_MESSAGES.has(e.message.trim())) {
    return e.message;
  }

  return lookup(t, 'unknown');
}
